// src/pages/PropertyListPage.tsx
/**
 * PropertyListPage
 *
 * Displays properties filtered by draft/ready status.
 * Supports selection mode for bulk delete operations.
 * Draft = no completed video
 * Ready = has at least one completed video
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { PropertySubmenuMemo } from '@/components/property/PropertySubmenu';
import { PropertyCardMemo } from '@/components/property/PropertyCard';
import { SelectionActionBar } from '@/components/property/SelectionActionBar';
import { BulkDeleteDialog } from '@/components/property/BulkDeleteDialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Building, Loader2, Plus } from 'lucide-react';
import type { IPropertyListPageProps, IPropertyListItem } from '@/@types/property';
import type { Id } from '../../convex/_generated/dataModel';

export const PropertyListPage = ({ filter }: IPropertyListPageProps) => {
  const navigate = useNavigate();
  const properties = useQuery(api.property.queries.listPropertiesByVideoStatus, {
    filter,
  });

  // Selection mode state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<Id<'propertyListings'>>>(new Set());
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteMultiple = useMutation(api.property.mutations.deleteMultiplePropertyListings);

  const isLoading = properties === undefined;
  const isEmpty = properties?.length === 0;

  const handlePropertyClick = (propertyId: Id<'propertyListings'>) => {
    navigate(`/property/${propertyId}`);
  };

  const handleToggleSelectionMode = () => {
    if (isSelectionMode) {
      // Exit selection mode and clear selections
      setIsSelectionMode(false);
      setSelectedIds(new Set());
    } else {
      setIsSelectionMode(true);
    }
  };

  const handleSelectToggle = useCallback((propertyId: Id<'propertyListings'>) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(propertyId)) {
        next.delete(propertyId);
      } else {
        next.add(propertyId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = () => {
    if (!properties) return;

    if (selectedIds.size === properties.length) {
      // Deselect all
      setSelectedIds(new Set());
    } else {
      // Select all
      setSelectedIds(new Set(properties.map((p: IPropertyListItem) => p._id)));
    }
  };

  const handleBulkDeleteClick = () => {
    setIsBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteMultiple({ propertyIds: Array.from(selectedIds) });
      setIsBulkDeleteDialogOpen(false);
      setSelectedIds(new Set());
      setIsSelectionMode(false);
    } catch (error) {
      console.error('Failed to delete properties:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const isAllSelected = properties
    ? selectedIds.size === properties.length && properties.length > 0
    : false;
  const isPartiallySelected = selectedIds.size > 0 && !isAllSelected;

  return {
    submenu: <PropertySubmenuMemo />,
    mainContent: (
      <div className="h-full bg-background overflow-auto">
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {filter === 'draft' ? 'Draft Properties' : 'Ready Properties'}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {filter === 'draft'
                    ? 'Properties without a completed video'
                    : 'Properties with completed videos ready for use'}
                </p>
              </div>

              {/* Header Actions */}
              <div className="flex items-center gap-2">
                {/* New Property Button */}
                <Button size="sm" onClick={() => navigate('/property/new')} className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  New Property
                </Button>

                {/* Select/Cancel Button */}
                {!isLoading && !isEmpty && (
                  <Button
                    variant={isSelectionMode ? 'outline' : 'secondary'}
                    size="sm"
                    onClick={handleToggleSelectionMode}
                  >
                    {isSelectionMode ? 'Cancel' : 'Select'}
                  </Button>
                )}
              </div>
            </div>

            {/* Select All (shown in selection mode) */}
            {isSelectionMode && properties && properties.length > 0 && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                <Checkbox
                  checked={isAllSelected}
                  data-state={
                    isPartiallySelected ? 'indeterminate' : isAllSelected ? 'checked' : 'unchecked'
                  }
                  onClick={handleSelectAll}
                  className="h-5 w-5"
                />
                <span className="text-sm text-muted-foreground">
                  {isAllSelected ? 'Deselect all' : `Select all (${properties.length})`}
                </span>
              </div>
            )}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
            </div>
          )}

          {/* Empty State */}
          {!isLoading && isEmpty && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Building className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">
                No {filter === 'draft' ? 'draft' : 'ready'} properties
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                {filter === 'draft'
                  ? 'No properties in progress. Click + New to scrape a listing.'
                  : 'No completed videos yet. Generate videos from your draft properties.'}
              </p>
            </div>
          )}

          {/* Property Grid */}
          {!isLoading && !isEmpty && properties && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {properties.map((property: IPropertyListItem) => (
                <PropertyCardMemo
                  key={property._id}
                  property={property}
                  onClick={handlePropertyClick}
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedIds.has(property._id)}
                  onSelectToggle={handleSelectToggle}
                />
              ))}
            </div>
          )}

          {/* Selection Action Bar */}
          {isSelectionMode && (
            <SelectionActionBar
              selectedCount={selectedIds.size}
              onDelete={handleBulkDeleteClick}
              isDeleting={isDeleting}
            />
          )}

          {/* Bulk Delete Dialog */}
          <BulkDeleteDialog
            isOpen={isBulkDeleteDialogOpen}
            onClose={() => setIsBulkDeleteDialogOpen(false)}
            onConfirm={handleBulkDeleteConfirm}
            isDeleting={isDeleting}
            count={selectedIds.size}
          />
        </div>
      </div>
    ),
  };
};
